import { Button, ButtonSize, ButtonVariant, Modal, Text, TextVariants } from "@next-platform/client-common-spicy-ui";
import { CHARACTER_VISIBILITY, ICharacter, NotificationType, useTranslation } from "@next-platform/client-common-core";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { BlockerFunction } from "react-router-dom";
import { useLocation, useNavigate } from "react-router-dom";
import ReactRouterPrompt from "@/components/common/ReactRouterPrompt";

import DiscardChangesModalV2 from "@/components/common/DiscardChangesModalV2";
import { GroupMembersChatBot, MembersGroupChat, MembersGroupChatDisplayMode } from "@/components/MembersGroupChat";
import { CharacterItemAction, DisplayContext, HeaderComponentType, MemberSelectConfig, ViewMode } from "@/components/search/types";
import MembersListing from "@/components/search/v2/MembersListing";
import Trans from "@/components/Trans/Trans";
import { useMaxGroupMembers } from "@/hooks/useMaxGroupMembers";
import useToast from "@/hooks/useToast";
import { useGroupChatForm } from "@/pages/Groupchat/GroupChatFormContext";
import { shouldBlockGroupMembersNavigation } from "@/pages/GroupChatMembers/shouldBlockGroupMembersNavigation";
import { AvatarPreset } from "@/types";
import { cn, getAvatarUrl } from "@/utils";

export const GroupChatMembers = () => {
  const { t } = useTranslation();
const { displayToast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const { maxMembers } = useMaxGroupMembers();
  const { members, setMembers, groupVisibility } = useGroupChatForm();
  const [isShowMinimumModal, setIsShowMinimumModal] = useState(false);
  const visibility = groupVisibility || CHARACTER_VISIBILITY.PUBLIC;
  const fromPathRef = useRef<string | null>(null);
  const initialMembersRef = useRef<GroupMembersChatBot[]>(members.map((member: GroupMembersChatBot) => ({ ...member })));
  const skipUnsavedChangesPromptRef = useRef(false);

  useEffect(() => {
    if (!fromPathRef.current) {
      fromPathRef.current = (location.state as { fromPath?: string })?.fromPath || null;
    }
  }, [location.state]);

  const hasUnsavedChanges = useMemo(() => {
    const initialMembers = initialMembersRef.current;
    if (members.length !== initialMembers.length) return true;

    if (!members.length && !initialMembers.length) return false;

    const currentIds = new Set(members.map((member: GroupMembersChatBot) => member.id));
    const initialIds = new Set(initialMembers.map((member: GroupMembersChatBot) => member.id));

    return currentIds.size !== initialIds.size || [...currentIds].some((id: string) => !initialIds.has(id));
  }, [members]);

  useEffect(() => {
    if (hasUnsavedChanges) {
      skipUnsavedChangesPromptRef.current = false;
    }
  }, [hasUnsavedChanges]);

  const shouldBlockNavigation = useCallback(
    ({ currentLocation, nextLocation }: Parameters<BlockerFunction>[0]) => {
      return shouldBlockGroupMembersNavigation({
        skipUnsavedChangesPrompt: skipUnsavedChangesPromptRef.current,
        hasUnsavedChanges,
        currentPathname: currentLocation.pathname,
        nextPathname: nextLocation.pathname,
      });
    },
    [hasUnsavedChanges],
  );

  const navigateBack = useCallback(() => {
    if (fromPathRef.current) {
      navigate(fromPathRef.current, { replace: true });
    } else {
      navigate(-1);
    }
  }, [navigate]);

  const restoreInitialMembers = useCallback(() => {
    setMembers(initialMembersRef.current.map((member: GroupMembersChatBot) => ({ ...member })));
  }, [setMembers]);

  const handleSave = () => {
    if (members.length < 2) {
      setIsShowMinimumModal(true);
      return;
    }
    skipUnsavedChangesPromptRef.current = true;
    navigateBack();
  };

  const handleForceSave = () => {
    setIsShowMinimumModal(false);
    skipUnsavedChangesPromptRef.current = true;
    navigateBack();
  };

  const handleRemoveMember = (memberId: string) => {
    setMembers(members.filter((member: GroupMembersChatBot) => member.id !== memberId));
  };

  const handleItemAction = (action: CharacterItemAction, character: ICharacter | undefined) => {
    if (!character) return;

    const characterId = character.character_id || character.id;

    if (action === CharacterItemAction.REMOVE) {
      setMembers(members.filter((member: GroupMembersChatBot) => member.id !== characterId));
      return;
    }

    if (action !== CharacterItemAction.ADD) return;

    const isAlreadySelected = members.some((member: GroupMembersChatBot) => member.id === characterId);
    const hasReachedMembersLimit = members.length >= maxMembers;
    if (isAlreadySelected || hasReachedMembersLimit) return;

    const newMember: GroupMembersChatBot = {
      id: characterId,
      name: character.name,
      avatar: getAvatarUrl(character.avatar_url || "", { preset: AvatarPreset.AVATAR_256X256 }) || "",
      avatarIsNsfw: character.avatar_is_nsfw || false,
      visibility: character.visibility || CHARACTER_VISIBILITY.PUBLIC,
      hasError: false,
    };

    setMembers([...members, newMember]);
  };

  const groupChatHeader = (
    <MembersGroupChat
      title={t("createGroup:form.field.groupMembers.title")}
      helperText={t("voice:groupMembers.helper")}
      members={members}
      maxMembers={maxMembers}
      mode={MembersGroupChatDisplayMode.EDITABLE}
      onSave={handleSave}
      onRemoveMember={handleRemoveMember}
      disabled={false}
      visibility={visibility}
    />
  );

  const memberSelectConfig: MemberSelectConfig = {
    headerComponent: HeaderComponentType.CUSTOM,
    customHeader: groupChatHeader,
    context: DisplayContext.MEMBER_SELECT,
    viewMode: ViewMode.HORIZONTAL_SELECTABLE,
    onItemAction: handleItemAction,
    hideNsfwToggle: true,
    hideWaitingQueue: true,
    hideBannedBanner: true,
    showDataSourceDropdown: true,
    selectedItems: members.map((member: GroupMembersChatBot) => member.id),
    maxItemsToSelect: maxMembers,
    onLimitReached: (maxCount: number) => {
      displayToast({
        message: t("createGroup:toast.groupMembers.maxReached", { maxCount }),
        type: NotificationType.error,
      });
    },
  };

  return (
    <div className={cn("w-full mx-auto", "max-w-[980px]")}>
      <ReactRouterPrompt when={shouldBlockNavigation}>
        {({ isActive, onConfirm, onCancel }) =>
          isActive ? (
            <DiscardChangesModalV2
              heading={t("common:modal.unsavedChanges.title")}
              description={t("common:modal.unsavedChanges.message")}
              isVisible={true}
              onUserConfirmation={() => {
                restoreInitialMembers();
                onConfirm();
              }}
              handleClose={onCancel}
            />
          ) : null
        }
      </ReactRouterPrompt>

      <div className="bg-uiBackground sm:p-3 rounded-lg border-solid border-element">
        <MembersListing config={memberSelectConfig} useStoreTagFilters={true} />
      </div>

      <Modal
        title={t("createGroup:modal.groupMembers.minimumRequired.title")}
        open={isShowMinimumModal}
        onOpenChange={() => setIsShowMinimumModal(false)}
        className="gap-4"
      >
        <div className="text-center">
          <Text variant={TextVariants.LABEL_LARGE_REGULAR} className="text-text1">
            <Trans i18nKey="createGroup:modal.groupMembers.minimumRequired.message" values={{ count: members.length }} />
          </Text>
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <Button variant={ButtonVariant.OUTLINED} size={ButtonSize.LARGE} onClick={handleForceSave}>
            <Trans i18nKey="createGroup:modal.groupMembers.minimumRequired.saveAnyway" />
          </Button>
          <Button size={ButtonSize.LARGE} onClick={() => setIsShowMinimumModal(false)}>
            <Trans i18nKey="common:cancel" />
          </Button>
        </div>
      </Modal>
    </div>
  );
};
